import React, { useEffect, useState, useRef, Component } from 'react';
import { MessageCircleIcon, XIcon, SendIcon, SparklesIcon } from 'lucide-react';
import { useOrganization } from '../contexts/OrganizationContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
/**
 * Message Interface
 * Represents a chat message in the conversation
 */
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  resources?: ChatResource[];
  suggestions?: string[];
}
{}
/**
 * Chat Resource Interface
 * Represents additional resources returned by the chatbot
 */
interface ChatResource {
  type: 'document' | 'url';
  document?: {
    title: string;
    id: string;
    read: boolean;
  };
  url?: {
    title: string;
    url: string;
    favicon: string;
  };
}
{}
/**
 * Assessment Context Interface
 * Provides context about current assessment data being viewed
 */
interface AssessmentContext {
  type: 'individual' | 'group' | 'trends';
  data?: any;
}
/**
 * ChatbotWidget Props
 */
interface ChatbotWidgetProps {
  assessmentContext?: AssessmentContext;
}
/**
 * API Response Interface
 */
interface ChatbotAPIResponse {
  text: {
    content: string;
    resources?: ChatResource[];
  };
}
{}
/**
 * Get contextual suggestions based on organization and conversation
 */
function getContextualSuggestions(isKyivMentalGuard: boolean, hasAssessmentContext: boolean): string[] {
  if (isKyivMentalGuard) {
    // Ukrainian suggestions for Kyiv Mental Guard
    if (hasAssessmentContext) {
      return ['Як я можу підтримати учня з цими результатами?', 'Які наступні кроки ви рекомендуєте?', 'Чи є тривожні ознаки в цих даних?', 'Як пояснити ці результати батькам?'];
    }
    return ['Розкажіть про емоційне благополуччя', 'Як розпізнати ознаки стресу?', 'Які ресурси доступні для підтримки?', "Поради щодо психічного здоров'я"];
  }
  // English suggestions for other organizations
  if (hasAssessmentContext) {
    return ['How can I support a student with these results?', 'What are the recommended next steps?', 'Are there any concerning patterns in this data?', 'How should I explain these results to parents?'];
  }
  return ['Tell me about emotional wellbeing', 'How to recognize signs of stress?', 'What resources are available for support?', 'Mental health tips for educators'];
}
{}
/**
 * ChatbotWidget Component
 * AI-powered chatbot widget for assessment interpretation and support
 * Connected to Sprinklenet Knowledge Spaces API with enhanced markdown rendering
 */
export function ChatbotWidget({
  assessmentContext
}: ChatbotWidgetProps) {
  const {
    currentOrganization
  } = useOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(0);
  const isKyivMentalGuard = currentOrganization.id === 'org-kmg';
  // Reset chatbot when organization changes
  useEffect(() => {
    // Generate new session ID for the new organization
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    localStorage.setItem('drBillSessionId', newSessionId);
    // Clear messages to trigger welcome message
    setMessages([]);
    // Reset input
    setInputValue('');
  }, [currentOrganization.id]);
  // Determine which chatbot endpoint to use based on organization
  const getChatbotEndpoint = () => {
    if (isKyivMentalGuard) {
      // Kyiv Mental Guard specific chatbot
      return 'https://spaces.sprinklenet.com/api/chatbots/68dd7419e02a7ca4d6b64c11/chat';
    }
    // General Dr. Bill chatbot for all other organizations
    return 'https://spaces.sprinklenet.com/api/chatbots/68f6ba0e11ce1212673e6806/chat';
  };
  // Generate or retrieve session ID
  useEffect(() => {
    const storedSessionId = localStorage.getItem('drBillSessionId');
    if (storedSessionId) {
      setSessionId(storedSessionId);
    } else {
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      localStorage.setItem('drBillSessionId', newSessionId);
    }
  }, []);
  // Smart scroll: only scroll to bottom when user sends a message
  useEffect(() => {
    if (messages.length > lastMessageCountRef.current) {
      const lastMessage = messages[messages.length - 1];
      // Only auto-scroll when user sends a message
      if (lastMessage?.role === 'user') {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'smooth'
        });
      }
      // For assistant messages, don't scroll - let user read from top
    }
    lastMessageCountRef.current = messages.length;
  }, [messages]);
  // Initialize with welcome message
  useEffect(() => {
    if (sessionId && messages.length === 0) {
      const welcomeContent = assessmentContext ? isKyivMentalGuard ? 'Вітаю! Я Dr. Bill, ваш асистент з емоційного благополуччя. Я бачу, що ви переглядаєте дані оцінювання. Як я можу допомогти вам зрозуміти ці результати?' : "Hello! I'm Dr. Bill, your emotional wellbeing assistant. I can see you're reviewing assessment data. How can I help you understand these results?" : isKyivMentalGuard ? 'Вітаю! Я Dr. Bill, ваш асистент з емоційного благополуччя. Я тут, щоб допомогти відповісти на питання про емоційне благополуччя та переглянути оцінювання. Як я можу вам допомогти сьогодні?' : "Hello! I'm Dr. Bill, your emotional wellbeing assistant. I'm here to help answer questions about emotional wellbeing and review assessments. How can I assist you today?";
      const welcomeMessage: Message = {
        id: '1',
        role: 'assistant',
        content: welcomeContent,
        timestamp: new Date(),
        suggestions: getContextualSuggestions(isKyivMentalGuard, !!assessmentContext)
      };
      setMessages([welcomeMessage]);
    }
  }, [sessionId, assessmentContext, isKyivMentalGuard]);
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  /**
   * Send message to Sprinklenet Knowledge Spaces API
   */
  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputValue;
    if (!textToSend.trim() || !sessionId) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: textToSend,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    try {
      const endpoint = getChatbotEndpoint();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          text: textToSend
        })
      });
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      const data: ChatbotAPIResponse = await response.json();
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.text.content,
        timestamp: new Date(),
        resources: data.text.resources,
        suggestions: getContextualSuggestions(isKyivMentalGuard, !!assessmentContext)
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error communicating with Dr. Bill:', error);
      // Fallback error message
      const errorContent = isKyivMentalGuard ? 'Вибачте, але у мене виникли проблеми з підключенням. Будь ласка, спробуйте ще раз через мить.' : "I apologize, but I'm having trouble connecting right now. Please try again in a moment.";
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: errorContent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion);
  };
  return <>
      {/* Floating Chat Button */}
      {!isOpen && <>
          <button onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full shadow-2xl hover:shadow-blue-500/50 hover:scale-110 transition-all duration-300 flex items-center justify-center z-50 group" aria-label="Open Dr. Bill chat" data-id="element-101">
            <MessageCircleIcon className="w-7 h-7 text-white" data-id="element-102" />
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" data-id="element-103" />
            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" data-id="element-104">
              {isKyivMentalGuard ? 'Чат з Dr. Bill' : 'Chat with Dr. Bill'}
            </div>
          </button>
        </>}
      {/* Chat Window */}
      {isOpen && <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border-2 border-gray-200" data-id="element-105">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-2xl flex items-center justify-between" data-id="element-106">
            <div className="flex items-center gap-3" data-id="element-107">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center" data-id="element-108">
                <SparklesIcon className="w-6 h-6 text-white" data-id="element-109" />
              </div>
              <div data-id="element-110">
                <div className="text-white font-bold text-lg" data-id="element-111">Dr. Bill</div>
                <div className="text-blue-100 text-xs flex items-center gap-1" data-id="element-112">
                  <div className="w-2 h-2 bg-green-400 rounded-full" data-id="element-113" />
                  {isKyivMentalGuard ? 'Онлайн' : 'Online'}
                </div>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white transition-colors p-1" aria-label="Close chat" data-id="element-114">
              <XIcon className="w-5 h-5" data-id="element-115" />
            </button>
          </div>
          {/* Context Banner */}
          {assessmentContext && <>
              <div className="px-4 py-2 bg-blue-50 border-b border-blue-100" data-id="element-116">
                <div className="text-xs text-blue-700 font-medium" data-id="element-117">
                  📊 {isKyivMentalGuard ? 'Перегляд' : 'Viewing'}{' '}
                  {assessmentContext.type === 'individual' ? isKyivMentalGuard ? 'індивідуальних' : 'individual' : assessmentContext.type === 'group' ? isKyivMentalGuard ? 'групових' : 'group' : isKyivMentalGuard ? 'трендових' : 'trends'}{' '}
                  {isKyivMentalGuard ? 'даних оцінювання' : 'assessment data'}
                </div>
              </div>
            </>}
          {/* Messages */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-118">
            {messages.map(message => <>
                <div key={message.id} data-id="element-119">
                  <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-120">
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${message.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'}`} data-id="element-121">
                      {/* Markdown Content */}
                      <div className={`text-sm leading-relaxed markdown-content ${message.role === 'user' ? 'text-white' : 'text-gray-900'}`} data-id="element-122">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                    // Paragraphs
                    p: ({
                      children
                    }) => <p className="mb-2 last:mb-0" data-id="element-124">{children}</p>,
                    // Headings
                    h1: ({
                      children
                    }) => <h1 className="text-lg font-bold mb-2 mt-3 first:mt-0" data-id="element-125">
                                {children}
                              </h1>,
                    h2: ({
                      children
                    }) => <h2 className="text-base font-bold mb-2 mt-3 first:mt-0" data-id="element-126">
                                {children}
                              </h2>,
                    h3: ({
                      children
                    }) => <h3 className="text-sm font-bold mb-2 mt-2 first:mt-0" data-id="element-127">
                                {children}
                              </h3>,
                    // Lists - Enhanced with better spacing and alignment
                    ul: ({
                      children
                    }) => <ul className="space-y-1.5 mb-3 pl-1" data-id="element-128">
                                {children}
                              </ul>,
                    ol: ({
                      children
                    }) => <ol className="space-y-1.5 mb-3 pl-1" data-id="element-129">
                                {children}
                              </ol>,
                    li: ({
                      children,
                      ordered
                    }) => {
                      const markerColor = message.role === 'user' ? 'text-white' : 'text-gray-700';
                      return <li className={`flex gap-2 ${markerColor}`} data-id="element-130">
                                  <span className="flex-shrink-0 font-medium select-none" data-id="element-131">
                                    {ordered ? '•' : '•'}
                                  </span>
                                  <span className="flex-1" data-id="element-132">{children}</span>
                                </li>;
                    },
                    // Code
                    code: ({
                      children,
                      className
                    }) => {
                      const isInline = !className;
                      const codeClass = message.role === 'user' ? 'bg-blue-700' : 'bg-gray-200';
                      return isInline ? <code className={`px-1.5 py-0.5 rounded text-xs font-mono ${codeClass}`} data-id="element-133">
                                  {children}
                                </code> : <code className={`block px-3 py-2 rounded text-xs font-mono overflow-x-auto mb-2 ${codeClass}`} data-id="element-134">
                                  {children}
                                </code>;
                    },
                    // Blockquotes
                    blockquote: ({
                      children
                    }) => {
                      const borderClass = message.role === 'user' ? 'border-blue-400' : 'border-gray-400';
                      return <blockquote className={`border-l-4 pl-3 py-1 my-2 italic ${borderClass}`} data-id="element-135">
                                  {children}
                                </blockquote>;
                    },
                    // Strong/Bold
                    strong: ({
                      children
                    }) => <strong className="font-bold" data-id="element-136">{children}</strong>,
                    // Emphasis/Italic
                    em: ({
                      children
                    }) => <em className="italic" data-id="element-137">{children}</em>,
                    // Links
                    a: ({
                      href,
                      children
                    }) => {
                      const linkClass = message.role === 'user' ? 'text-blue-100' : 'text-blue-600';
                      return <a href={href} target="_blank" rel="noopener noreferrer" className={`underline hover:no-underline ${linkClass}`} data-id="element-138">
                                  {children}
                                </a>;
                    },
                    // Horizontal Rule
                    hr: () => {
                      const hrClass = message.role === 'user' ? 'border-blue-400' : 'border-gray-300';
                      return <hr className={`my-3 ${hrClass}`} data-id="element-139" />;
                    }
                  }} data-id="element-123">
                          {message.content}
                        </ReactMarkdown>
                      </div>
                      {/* Display resources if available */}
                      {message.resources && message.resources.length > 0 && <div className="mt-3 space-y-2" data-id="element-140">
                          {message.resources.map((resource, idx) => <div key={idx} className="text-xs bg-white/10 rounded-lg p-2" data-id="element-141">
                              {resource.type === 'document' && resource.document && <div className="flex items-center gap-2" data-id="element-142">
                                    <span data-id="element-143">📄</span>
                                    <span data-id="element-144">{resource.document.title}</span>
                                  </div>}
                              {resource.type === 'url' && resource.url && <a href={resource.url.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:underline" data-id="element-145">
                                  {resource.url.favicon && <img src={resource.url.favicon} alt="" className="w-4 h-4" data-id="element-146" />}
                                  <span data-id="element-147">{resource.url.title}</span>
                                </a>}
                            </div>)}
                        </div>}
                      <div className={`text-xs mt-2 ${message.role === 'user' ? 'text-blue-100' : 'text-gray-500'}`} data-id="element-148">
                        {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </div>
                    </div>
                  </div>
                  {/* Suggestion Chips */}
                  {message.role === 'assistant' && message.suggestions && message.suggestions.length > 0 && message.id === messages[messages.length - 1].id && <div className="mt-3 flex flex-wrap gap-2" data-id="element-149">
                        {message.suggestions.map((suggestion, idx) => <button key={idx} onClick={() => handleSuggestionClick(suggestion)} className="px-3 py-1.5 text-xs bg-white border-2 border-blue-200 text-blue-700 rounded-full hover:bg-blue-50 hover:border-blue-300 transition-colors" data-id="element-150">
                            {suggestion}
                          </button>)}
                      </div>}
                </div>
              </>)}
            {/* Typing Indicator */}
            {isTyping && <div className="flex justify-start" data-id="element-151">
                <div className="bg-gray-100 rounded-2xl px-4 py-3" data-id="element-152">
                  <div className="flex gap-1" data-id="element-153">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" data-id="element-154" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" data-id="element-155" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" data-id="element-156" />
                  </div>
                </div>
              </div>}
            <div ref={messagesEndRef} data-id="element-157" />
          </div>
          {/* Input */}
          <div className="p-4 border-t border-gray-200" data-id="element-158">
            <div className="flex gap-2" data-id="element-159">
              <input ref={inputRef} type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder={isKyivMentalGuard ? 'Введіть ваше повідомлення тут...' : 'Ask Dr. Bill a question...'} className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-160" />
              <button onClick={() => handleSendMessage()} disabled={!inputValue.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" aria-label="Send message" data-id="element-161">
                <SendIcon className="w-4 h-4" data-id="element-162" />
              </button>
            </div>
            <div className="text-xs text-gray-500 mt-2 text-center" data-id="element-163">
              {isKyivMentalGuard ? 'Dr. Bill - AI-асистент з емоційного благополуччя' : 'Dr. Bill is an AI assistant for emotional wellbeing support'}
            </div>
          </div>
        </div>}
    </>;
}